import { useMemo } from 'react';
import { Form } from 'antd';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useEvaluationCategoryQuestions } from './useEvaluationCategoryQuestions';

interface EvaluationCategoryQuestionsProps {
	goToSubStep: (step: number) => void;
}

type EvaluationResult = {
	showMandatoryTolerableInfo: boolean;
	showConditionalTolerableInfo: boolean;
	showConditionalNotTolerableInfo: boolean;
};

const { useWatch } = Form;

export const useInfoEvaluationCategories = ({ goToSubStep }: EvaluationCategoryQuestionsProps): EvaluationResult => {
	const { form, handleSetIsTolerable } = useResolution886Report();
	const { category } = useEvaluationCategoryQuestions({ goToSubStep });
	const allFormValues = useWatch([], form);

	const evaluationResult = useMemo((): EvaluationResult => {
		if (!category) {
			return {
				showMandatoryTolerableInfo: false,
				showConditionalTolerableInfo: false,
				showConditionalNotTolerableInfo: false
			};
		}
		const mandatoryAnswers = category.mandatory_questions.map((question) => ({
			key: question.key,
			value: form.getFieldValue(['questions', 'mandatory_questions', question.key])
		}));

		const conditionalAnswers = category.conditional_questions.map((question) => ({
			key: question.key,
			value: form.getFieldValue(['questions', 'conditional_questions', question.key])
		}));

		const allMandatoryAnswered = mandatoryAnswers.every(
			(answer) => answer.value === 'yes' || answer.value === 'no'
		);
		const allMandatoryNo = mandatoryAnswers.every((answer) => answer.value === 'no');

		const hasAnyConditionalYes = conditionalAnswers.some((answer) => answer.value === 'yes');
		const allConditionalAnswered = conditionalAnswers.every(
			(answer) => answer.value === 'yes' || answer.value === 'no'
		);

		return {
			showMandatoryTolerableInfo: allMandatoryAnswered && allMandatoryNo,
			showConditionalTolerableInfo: allConditionalAnswered && !hasAnyConditionalYes,
			showConditionalNotTolerableInfo: hasAnyConditionalYes
		};
	}, [form, category, allFormValues]);

	handleSetIsTolerable(
		evaluationResult.showMandatoryTolerableInfo || evaluationResult.showConditionalTolerableInfo ? true : false
	);

	return evaluationResult;
};
